// ─────────────────────────────────────────────────────────────────────────────
// Service-area pages
// ─────────────────────────────────────────────────────────────────────────────
// One entry per city page. Each has genuinely distinct copy — not one template
// with the city name swapped, which is the pattern search engines discount and
// readers see through immediately.
//
// WHAT IS AND ISN'T ALLOWED IN HERE
//  ✔ Public municipal facts: which region a city sits in, that building
//    permits are issued by the local municipality, broad housing-stock history.
//  ✔ How that housing stock changes the renovation work — the genuinely useful
//    local angle, and the reason these pages deserve to exist at all.
//  ✘ Claims about projects completed in a named city or neighbourhood. Which
//    project sat where has never been confirmed, so nothing here says it.
//  ✘ Invented neighbourhood name-drops used purely as keyword filler.
//  ✘ Local review counts, "trusted by X homeowners in Y", or years serving a
//    named city.
//
// The Ontario-wide additional-residential-unit point on the basement-heavy
// pages refers to the province's current framework permitting additional units
// on most urban residential lots; the copy is careful to say a building permit
// is still required and that the municipality sets the specifics.

import { SERVICE_AREAS } from "./company";

export const LOCATIONS = [
  {
    slug: "pickering",
    name: "Pickering",
    formalName: "Pickering, Ontario",
    h1: "Home Renovation Services in Pickering, Ontario",
    title: "Home Renovation Contractor in Pickering, Ontario",
    metaDescription:
      "Five Lights Reno is a renovation contractor based in Pickering, Ontario — kitchens, bathrooms, legal basements, flooring, tile and painting across the city and Durham Region.",
    eyebrow: "Service area — our home city",
    lede: "Pickering is where we're based. It's the shortest drive we make, and the city whose housing stock we know best.",
    hero: "basementHallway",
    intro: [
      "Five Lights Reno works out of Pickering, so most of our projects start with a short drive rather than a trip across the GTA. That matters more than it sounds: it's the difference between a trade coming back the same afternoon to fix something and it waiting until next week.",
      "Pickering covers a wide range of housing. The established neighbourhoods south and west of the 401 are largely homes built between the 1960s and the 1990s — solid construction, but with kitchens, bathrooms and basements now well past their design life. Newer development in the north of the city brings the opposite problem: a well-built house with an unfinished basement and a builder-grade finish level throughout.",
      "We work on both. The older homes usually need layout and services rethought as well as finishes replaced. The newer ones tend to be about finishing what the builder left, and lifting the specification to something that doesn't feel standard.",
    ],
    angleTitle: "What renovating in Pickering usually involves",
    angle: [
      {
        title: "Homes from the 60s through the 90s",
        body: "Closed-off kitchens, small original bathrooms, and basements that were never finished properly. Layout changes usually deliver more than new finishes do — and once a wall is coming out, it's worth checking what the electrical and plumbing look like behind it.",
      },
      {
        title: "Newer builds with unfinished lower levels",
        body: "A large empty basement, roughed-in plumbing and nothing else. These are the most straightforward legal basement conversions we do, because the framing plan can start from scratch rather than working around somebody else's.",
      },
      {
        title: "Permits come from the City of Pickering",
        body: "Structural work, a legal second unit, and most plumbing and electrical changes need a building permit from the City. We make the application and coordinate inspections as part of the project.",
      },
    ],
    services: ["legal-basement", "bath", "kitchen", "flooring"],
    faqs: [
      {
        question: "Are you actually based in Pickering?",
        answer:
          "Yes — Pickering is our home city, and it's where most of our work is. It means shorter travel, easier scheduling, and a crew that can come back quickly if something needs attention after a job is finished.",
      },
      {
        question: "Can you finish a basement in a newer Pickering home?",
        answer:
          "Yes, and newer homes are usually the cleanest conversions to do. The basement is typically open, dry and roughed-in for plumbing, so the framing plan can be laid out around the finished layout you want rather than around existing partitions.",
      },
      {
        question: "Do I need a permit to renovate in Pickering?",
        answer:
          "For structural changes, a legal second unit, and most plumbing or electrical work, yes — the building permit comes from the City of Pickering. Purely cosmetic work like paint or flooring generally doesn't. We confirm which applies before quoting and handle the application where one is needed.",
      },
      {
        question: "How far do you travel from Pickering?",
        answer:
          "Ajax, Whitby and the rest of Durham Region are routine for us, and we work into Oshawa and west toward Toronto as well. If you're further out, ask — it's a scheduling question rather than a hard boundary.",
      },
    ],
  },

  {
    slug: "ajax",
    name: "Ajax",
    formalName: "Ajax, Ontario",
    h1: "Home Renovation Services in Ajax, Ontario",
    title: "Home Renovation Contractor in Ajax, Ontario",
    metaDescription:
      "Renovation contractor serving Ajax, Ontario — bathroom and kitchen renovations, legal basement conversions, flooring, tile and painting, from a Pickering-based crew next door.",
    eyebrow: "Service area — next door",
    lede: "Ajax sits directly east of us. It's a short drive, and a town where a very particular kind of renovation keeps coming up.",
    hero: "bathGlassDoor",
    intro: [
      "Ajax is the town immediately east of our base in Pickering, which makes it one of the easiest places for us to work — crews arrive early, deliveries land where they should, and coming back for a small item doesn't cost a day.",
      "The town's housing tells a clear story. Ajax grew fast after the Second World War and then again through the subdivision booms of the 1980s onward, so a large share of homes here were built within a couple of decades of each other. That means whole streets hit the same renovation milestones at the same time: bathrooms and kitchens that have reached the end of a second life, and basements that were finished once, decades ago, to a standard nobody would accept now.",
      "It also means we've seen the same floor plans repeatedly. When a homeowner describes the awkward half-wall between their kitchen and dining room, we usually already know what's inside it.",
    ],
    angleTitle: "What renovating in Ajax usually involves",
    angle: [
      {
        title: "Second-generation bathrooms",
        body: "Not the original 1980s bathroom — the one that replaced it in the early 2000s, now tired and often with waterproofing that predates modern practice. These almost always come apart back to the studs, because patching over the old work just moves the problem a few years down the road.",
      },
      {
        title: "Basements finished a long time ago",
        body: "Panelling, low-clearance dropped ceilings, and framing that sits directly against the foundation with no vapour control. Stripping it back is usually the right call, and it's the point at which a basement can be brought up to a standard that would actually pass inspection.",
      },
      {
        title: "Repeating subdivision floor plans",
        body: "The upside of a town built in waves: layout problems repeat. We can often tell you what a wall is doing and roughly what removing it involves before anything is opened up.",
      },
    ],
    services: ["bath", "legal-basement", "kitchen", "tile"],
    faqs: [
      {
        question: "Do you charge extra to work in Ajax?",
        answer:
          "No. Ajax is directly next to our base in Pickering, so travel doesn't materially change what a project costs.",
      },
      {
        question: "Our basement was finished in the 90s — can it be updated?",
        answer:
          "Usually the honest answer is to strip it back rather than work over it. Older basement finishing often lacks proper vapour control and has framing tight against the foundation. Taking it back gives you the ceiling height, insulation and egress you need — and it's the only route if you want the space to be legal.",
      },
      {
        question: "Can you convert an Ajax basement into a legal second unit?",
        answer:
          "Yes. Ontario's current framework allows additional residential units on most urban residential lots, but a building permit from the Town of Ajax is still required and the Town sets the specifics — egress windows, ceiling height, fire separation and a separate entrance. We handle the application and the inspections.",
      },
      {
        question: "How long is a full bathroom renovation in an older Ajax home?",
        answer:
          "Typically three to five weeks once plumbing is being moved. Older homes occasionally add time when what's behind the wall isn't what anyone expected — we'd rather tell you that upfront than discover it and quietly extend the schedule.",
      },
    ],
  },

  {
    slug: "whitby",
    name: "Whitby",
    formalName: "Whitby, Ontario",
    h1: "Home Renovation Services in Whitby, Ontario",
    title: "Home Renovation Contractor in Whitby, Ontario",
    metaDescription:
      "Renovation contractor working in Whitby and Brooklin, Ontario — kitchen and bathroom renovations, basement finishing, flooring, tile, drywall and painting.",
    eyebrow: "Service area — Whitby & Brooklin",
    lede: "Whitby asks for two different kinds of work in the same town: careful hands in older homes, and finishing what the builder left in newer ones.",
    hero: "tileFeatureWall",
    intro: [
      "Whitby is a genuinely mixed town to renovate in, and the approach changes depending on which part of it you're standing in.",
      "Around the older core and toward the lake there are homes with real age to them — plaster rather than drywall, floors that are no longer level, and framing that predates any standard dimension. That work rewards patience: scribing to an uneven wall, matching an existing trim profile, and resisting the urge to make everything square when the house never was.",
      "North of the 401 and up through Brooklin, the housing is much newer. There the job is usually about finishing space the builder handed over unfinished, and replacing builder-grade materials with something that doesn't look like every other house on the street.",
    ],
    angleTitle: "What renovating in Whitby usually involves",
    angle: [
      {
        title: "Older homes near the core",
        body: "Plaster walls, uneven floors and non-standard framing. Drywall and tile both have to be set to what's actually there rather than to a nominal dimension — and levelling a subfloor properly matters far more here than in a newer build.",
      },
      {
        title: "Newer homes in north Whitby and Brooklin",
        body: "Large unfinished basements and builder-grade finishes throughout. Straightforward to work in, and the place where upgrading tile, flooring and trim changes the feel of a house the most for the money.",
      },
      {
        title: "Permits come from the Town of Whitby",
        body: "Structural work, second units and most plumbing or electrical changes need a permit from the Town. We make the application and coordinate the inspection schedule as part of the project.",
      },
    ],
    services: ["kitchen", "flooring", "drywall-taping", "tile"],
    faqs: [
      {
        question: "Do you work in Brooklin as well as Whitby?",
        answer:
          "Yes. Brooklin is part of the Town of Whitby and we work there regularly — it's mostly newer housing, so the work skews toward finishing basements and upgrading builder-grade finishes.",
      },
      {
        question: "Our house has plaster walls, not drywall. Is that a problem?",
        answer:
          "Not a problem, but it does change the method. Plaster is repaired and blended differently from drywall, and cutting into it needs more care to avoid cracking the surrounding area. It's worth saying so at the quoting stage so the scope reflects the real work.",
      },
      {
        question: "Can you level an old floor before new flooring goes down?",
        answer:
          "Yes, and in an older Whitby home it's often the step that decides whether the finished floor looks right. Subfloor levelling and any squeak or fastening issues get sorted before material is ordered — none of it can be fixed afterwards.",
      },
      {
        question: "Do you take on smaller jobs in Whitby?",
        answer:
          "Yes. A single bathroom, one floor, a drywall repair after other work — those are all normal for us. If it's genuinely a half-day handyman task, we'll say so rather than pricing it as a project.",
      },
    ],
  },

  {
    slug: "durham-region",
    name: "Durham Region",
    formalName: "Durham Region, Ontario",
    h1: "Home Renovation Services Across Durham Region",
    title: "Home Renovation Contractor Across Durham Region, Ontario",
    metaDescription:
      "Five Lights Reno renovates homes across Durham Region — Pickering, Ajax, Whitby, Oshawa and beyond. Kitchens, bathrooms, legal basements, flooring, tile and painting.",
    eyebrow: "Service area — the region",
    lede: "Durham is eight municipalities and about as wide a range of housing as Ontario has. We work across it, with most of our projects in the western end.",
    hero: "staircaseAfter",
    isRegion: true,
    intro: [
      "Durham Region runs from the Pickering border east past Oshawa and Clarington, and north through Uxbridge, Scugog and Brock. Between them, those municipalities cover just about every kind of house Southern Ontario builds: post-war bungalows, 1980s subdivisions, heritage homes near the older town cores, and new construction still going up.",
      "We're based in Pickering, and the bulk of our work sits in the western end of the region — Pickering, Ajax and Whitby — with projects in Oshawa as well. That concentration is deliberate. A crew that can be at a job in twenty minutes handles the small things properly; one that has to cross the region to hang a door tends not to come back for it.",
      "What doesn't change across the region is how we work: a walkthrough before any numbers, a written scope and timeline, permits handled where they're needed, and one crew that stays with your project rather than a rotating cast of subcontractors.",
    ],
    angleTitle: "How the region breaks down",
    angle: [
      {
        title: "Western Durham — where most of our work is",
        body: "Pickering, Ajax and Whitby. Dense, well-established housing with a lot of homes hitting the same renovation milestones at once, plus newer development with unfinished basements. This is our core service area.",
      },
      {
        title: "Oshawa and further east",
        body: "We take on projects here regularly. The housing skews older through much of the city, which usually means layout and services need as much thought as finishes do.",
      },
      {
        title: "North Durham and beyond",
        body: "Uxbridge, Scugog and Brock are a longer drive, and we'd rather have an honest conversation about scheduling than take on a project we can't service properly. Ask — it depends on the size of the job and what else is running.",
      },
    ],
    services: ["renovations", "legal-basement", "bath", "kitchen"],
    faqs: [
      {
        question: "Which parts of Durham Region do you cover?",
        answer:
          "Most of our work is in Pickering, Ajax and Whitby, and we take on projects in Oshawa as well. Further north and east is possible depending on the size of the project and our schedule — it's worth asking rather than assuming.",
      },
      {
        question: "Do you work outside Durham Region?",
        answer:
          "We work west toward Scarborough and Toronto as well. Beyond that it becomes a scheduling question, and we'd tell you honestly if a project were too far to service the way we'd want to.",
      },
      {
        question: "Does the permit process differ between municipalities?",
        answer:
          "The building permit comes from your local municipality — the City of Pickering, the Town of Ajax, the Town of Whitby, and so on — so the process and timelines vary a little between them. We handle the application wherever the project is.",
      },
      {
        question: "Can you handle a whole-home renovation, not just one room?",
        answer:
          "Yes. Full and partial home renovations are a core part of what we do, and because we self-perform most trades a larger scope doesn't mean handing your house to five different companies.",
      },
    ],
  },
];

export const getLocationBySlug = (slug) =>
  LOCATIONS.find((location) => location.slug === slug);

// Every location except the one passed in — used for the cross-linking block
// at the foot of each city page.
export const otherLocations = (slug) =>
  SERVICE_AREAS.filter((area) => area.slug !== slug);
